import React, {useEffect, useRef, useState} from 'react';
import {Image, Linking, View} from 'react-native';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import Box from '@/Atoms/Box';
import CCButton from '@/Atoms/CCButton';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes, vs} from '@/Utils/Dimensions';
import SlideModal from '@/Atoms/SlideModal';
import CCTextInput from '@/Atoms/CCTextInput';
import CCFlatlist from '@/Atoms/CCFlatlist';
import CCRadio from '@/Atoms/CCRadio/CCRadio';
import Spacer from '@/Atoms/Spacer';
import useFetchTournamentEligibility from '@/Hooks/GetTournaments/useFetchTournamentEligibility';
import useFetchTournamentQuestions from '@/Hooks/GetTournaments/useFetchTournamentQuestions';
import useSaveTournamentQuestionsResponse from '@/Hooks/GetTournaments/useSaveTournamentQuestionsResponse';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import CCLoader from '@/Atoms/CCLoader';
import useGeneratePaymentApi from '@/Hooks/SubscriptionApi/useGeneratePayment';
import {useOrderStatusApi} from '@/Hooks/BooksApi/useBooksApi';
// import useJoinTournament from '@/Hooks/TournamentsApi/useJoinTournament';
import {
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from '@/Utils/CommonUtils';

// Status ID constants from API
// 21 -> Charged, 26,27,22,34 -> Failed, 28,20,33,23 -> Pending, rest -> Processing
const STATUS_IDS = {
  CHARGED: [21],
  FAILED: [22, 26, 27, 34],
  PENDING: [20, 23, 28, 33],
} as const;

type PaymentStatus = 'CHARGED' | 'PENDING' | 'FAILED' | 'PROCESSING';

const isStatusId = (statusId: number, statusList: readonly number[]): boolean =>
  statusList.includes(statusId);

const getPaymentStatus = (statusId: number | undefined): PaymentStatus => {
  if (statusId === undefined) {
    return 'PROCESSING';
  }
  if (isStatusId(statusId, STATUS_IDS.CHARGED)) {
    return 'CHARGED';
  }
  if (isStatusId(statusId, STATUS_IDS.FAILED)) {
    return 'FAILED';
  }
  if (isStatusId(statusId, STATUS_IDS.PENDING)) {
    return 'PENDING';
  }
  return 'PROCESSING';
};

const isTerminalStatus = (statusId: number | undefined): boolean => {
  if (statusId === undefined) {
    return false;
  }
  return (
    isStatusId(statusId, STATUS_IDS.CHARGED) ||
    isStatusId(statusId, STATUS_IDS.FAILED)
  );
};

export type PaidTournamentRegistrationModalProps = {
  visible: boolean;
  tournament: any | null;
  userId: string;
  onClose: () => void;
  onRefreshDetails: () => void;
  orderId?: string | null;
  handleJoin: () => void;
};

const PaidTournamentRegistrationModal: React.FC<
  PaidTournamentRegistrationModalProps
> = ({
  visible,
  tournament,
  userId,
  onClose,
  onRefreshDetails,
  orderId,
  handleJoin,
}) => {
  const userData = useLoggedInUserStore(state => state.userData);
  const [stage, setStage] = useState(0);
  const [stageReached, setStageReached] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedTournament, setSelectedTournament] = useState<any | null>(
    null,
  );
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [isSubmittingQuestions, setIsSubmittingQuestions] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const registrationInitDoneRef = useRef(false);
  const [pendingOrder, setPendingOrder] = useState<any | null>(null);
  const [continuePaymentUrl, setContinuePaymentUrl] = useState<string | null>(
    null,
  );

  const {mutate: generatePayment} = useGeneratePaymentApi(
    async (paymentUrlData: any) => {
      if (paymentUrlData?.data?.content?.payment_links?.web) {
        const {
          order_id: generatedOrderId,
          payment_links: {web: paymentUrl},
          order_expiry: orderExpiry,
        } = paymentUrlData.data.content;

        const pendingData = {
          order_id: generatedOrderId,
          tournament_id: selectedTournament?.cct_id,
          tournament_registration_tid: selectedTournament?.registration_tid,
          payment_url: paymentUrl,
          order_expiry: orderExpiry,
          created_at: Date.now(),
        };

        await setLocalStorage(
          'pending_tournament_order',
          JSON.stringify(pendingData),
        );

        setPendingOrder(pendingData);
        setContinuePaymentUrl(paymentUrl);
        setStage(4);
        setIsPaymentProcessing(false);
        orderBookStatus({order_id: generatedOrderId});
        Linking.openURL(paymentUrl);
      } else if (paymentUrlData?.data?.success) {
        await removeLocalStorage('pending_tournament_order');
        onClose();
        setStage(0);
        onRefreshDetails();
      }
    },
    (error: any) => {
      console.log('Error generating payment:', error);
      setErrorMessage('Failed to generate payment. Please try again.');
      setIsPaymentProcessing(false);
    },
  );

  const {
    mutate: checkEligibility,
    data: eligibilityData,
    isLoading: eligibilityLoading,
  } = useFetchTournamentEligibility();
  const {
    mutate: getQuestions,
    data: questionsData,
    isLoading: questionLoading,
  } = useFetchTournamentQuestions();
  // const {mutate: joinTournamentRest} = useJoinTournament(onRefreshDetails);
  const {
    mutate: orderBookStatus,
    data: orderStatusResponse,
    isLoading: isStatusLoading,
  } = useOrderStatusApi();

  // Handle eligibility response - skip questions stage if not needed
  useEffect(() => {
    if (!eligibilityData) {
      return;
    }

    if (eligibilityData?.status) {
      if (!eligibilityData?.has_questions) {
        setStage(2);
        setStageReached(true);
      } else {
        getQuestions({
          tournament_id: String(selectedTournament?.registration_tid),
          user_id: Number(userId),
        });
        setStage(1);
      }
    } else if (eligibilityData?.club_id) {
      setErrorMessage(eligibilityData?.error);
      if (selectedTournament?.club) {
        setSelectedTournament({
          ...selectedTournament.club,
          registration_tid: eligibilityData?.club_id,
        });
      }
      checkEligibility({
        mobile_number: userData?.mobile_number,
        player_id: userData?.player,
        registration_tid: Number(eligibilityData?.club_id),
      });
    }
  }, [eligibilityData]);

  // Skip to entry fee if no questions
  useEffect(() => {
    if (questionsData && !questionsData?.questions?.length) {
      setStage(2);
      setStageReached(true);
    }
  }, [questionsData]);

  // Track stage progression
  useEffect(() => {
    if (stage > 0) {
      setStageReached(true);
    }
  }, [stage]);

  // Handle successful payment - join tournament
  useEffect(() => {
    const statusId = orderStatusResponse?.status_id;
    if (statusId === 21 && selectedTournament) {
      // const tournamentId = selectedTournament?.parent_id
      //   ? Number(selectedTournament.parent_id)
      //   : Number(selectedTournament?.id);
      // joinTournamentRest({
      //   tournamentId,
      //   playerId: Number(userData?.player),
      // });
      handleJoin();
    }
  }, [
    orderStatusResponse?.status_id,
    selectedTournament,
    userData?.player,
    handleJoin,
  ]);

  // Polling effect for order status (30-second interval)
  useEffect(() => {
    if (
      !pendingOrder?.order_id ||
      isTerminalStatus(orderStatusResponse?.status_id)
    ) {
      return;
    }

    const intervalId = setInterval(() => {
      orderBookStatus({order_id: pendingOrder.order_id});
    }, 30000);

    return () => clearInterval(intervalId);
  }, [pendingOrder?.order_id, orderStatusResponse?.status_id]);

  // Handle order status response - manage pending order lifecycle
  useEffect(() => {
    if (!orderStatusResponse || !pendingOrder) {
      return;
    }

    const statusId = orderStatusResponse?.status_id;
    const expiry = orderStatusResponse?.order_expiry;

    // CHARGED - success, clear and refresh
    if (statusId === 21) {
      removeLocalStorage('pending_tournament_order');
      setPendingOrder(null);
      setContinuePaymentUrl(null);
      onRefreshDetails();
      return;
    }

    // FAILED or expired - clear and allow retry
    if (
      isStatusId(statusId, STATUS_IDS.FAILED) ||
      (expiry && new Date(expiry).getTime() < Date.now())
    ) {
      removeLocalStorage('pending_tournament_order');
      setPendingOrder(null);
      setContinuePaymentUrl(null);
    }
  }, [orderStatusResponse, pendingOrder]);

  // Initialize on modal open
  useEffect(() => {
    if (!visible) {
      registrationInitDoneRef.current = false;
      setStageReached(false);
      return;
    }
    if (!tournament || orderId) {
      return;
    }
    if (registrationInitDoneRef.current) {
      return;
    }

    registrationInitDoneRef.current = true;
    setErrorMessage('');
    setSelectedTournament(tournament);

    if (tournament.is_series) {
      setStage(3);
    } else {
      setStage(0);
      checkEligibility({
        mobile_number: userData?.mobile_number,
        player_id: userData?.player,
        registration_tid: tournament?.registration_tid,
      });
    }
  }, [visible, tournament?.id, orderId, tournament]);

  // Check for existing pending order on mount
  useEffect(() => {
    if (!visible || !tournament) {
      return;
    }

    const checkAndSetPendingOrder = async () => {
      const pending = await getLocalStorage('pending_tournament_order');
      if (!pending) {
        return;
      }

      const pendingData = JSON.parse(pending);
      if (
        pendingData?.tournament_registration_tid !==
        tournament?.registration_tid
      ) {
        return;
      }

      // Check expiry
      if (pendingData?.order_expiry) {
        const expiryDate = new Date(pendingData.order_expiry).getTime();
        if (expiryDate < Date.now()) {
          await removeLocalStorage('pending_tournament_order');
          return;
        }
      }

      setSelectedTournament(tournament);
      orderBookStatus({order_id: pendingData.order_id});
      setPendingOrder(pendingData);
      setContinuePaymentUrl(pendingData.payment_url);
      setStage(4);
      setStageReached(true);
    };

    checkAndSetPendingOrder();
  }, [visible, tournament]);

  // Legacy polling for orderId from route (3-second interval, max 10)
  useEffect(() => {
    if (stage !== 4 || !orderId) {
      return;
    }
    if (isTerminalStatus(orderStatusResponse?.status_id)) {
      return;
    }
    if (pollCount >= 10) {
      return;
    }

    const timer = setTimeout(() => {
      orderBookStatus({order_id: orderId});
      setPollCount(prev => prev + 1);
    }, 3000);

    return () => clearTimeout(timer);
  }, [stage, orderId, orderStatusResponse?.status_id, pollCount]);

  // Handle orderId from route params
  useEffect(() => {
    if (!orderId || !tournament) {
      return;
    }
    setSelectedTournament(tournament);
    setStage(4);
    orderBookStatus({order_id: orderId});
  }, [orderId, tournament?.id]);

  const handlePayment = async (category: string, amount: number) => {
    await removeLocalStorage('pending_tournament_order');
    setPendingOrder(null);
    setContinuePaymentUrl(null);
    setIsPaymentProcessing(true);

    generatePayment({
      category_str: `INR ${amount} / ${category}`,
      customer_id: userData?.user_id,
      email: userData?.email,
      mobile_number: userData?.mobile_number,
      player_id: userData?.player,
      tournament_id: selectedTournament.cct_id,
      amount,
      return_url: typeof window !== 'undefined' ? window.location?.href : '',
    });
  };

  const handleClose = async () => {
    setStage(0);
    setStageReached(false);
    setErrorMessage('');
    setIsPaymentProcessing(false);
    setIsSubmittingQuestions(false);
    setPollCount(0);
    setPendingOrder(null);
    setContinuePaymentUrl(null);
    await removeLocalStorage('pending_tournament_order');
    onClose();
  };

  const paymentStatus = getPaymentStatus(orderStatusResponse?.status_id);

  return (
    <SlideModal
      type="side"
      visible={visible}
      modalHeaderTitle={
        stageReached && stage === 4
          ? 'Payment Status'
          : stageReached && stage === 3
          ? 'Series Registration'
          : stageReached && stage === 1
          ? 'Answer Questions'
          : 'Select Entry Fee'
      }
      onClose={handleClose}
      data-testid="paid-registration-modal">
      <View style={{padding: 30, flex: 1}} data-testid="modal-content">
        {eligibilityLoading || questionLoading || isStatusLoading ? (
          <View
            style={{flex: 1, justifyContent: 'center', alignItems: 'center'}}>
            <CCLoader loading data-testid="modal-loading" bgWhite />
          </View>
        ) : (
          <>
            {stage === 4 && !isStatusLoading && (
              <PaymentStatusModal
                status={paymentStatus}
                onClose={handleClose}
                pendingOrder={pendingOrder}
                onRetry={() => {
                  if (continuePaymentUrl) {
                    Linking.openURL(continuePaymentUrl);
                    setContinuePaymentUrl(null);
                  }
                }}
                isProcessing={isPaymentProcessing}
              />
            )}
            {stage === 3 && (
              <SeriesRegistrationModal tournament={selectedTournament} />
            )}
            {stage === 2 && (
              <EntryFeeSelect
                categories={eligibilityData?.category_fees || {}}
                errorMessage={errorMessage}
                handlePayment={handlePayment}
                isPaymentProcessing={isPaymentProcessing}
                continuePaymentUrl={continuePaymentUrl}
                pendingOrder={pendingOrder}
                setContinuePaymentUrl={setContinuePaymentUrl}
              />
            )}
            {stage < 2 && (
              <QuestionList
                questions={questionsData?.questions}
                userData={userData}
                selectedTournament={selectedTournament}
                setStage={setStage}
              />
            )}
          </>
        )}
      </View>
    </SlideModal>
  );
};

const QuestionList = ({
  questions = [],
  selectedTournament,
  userData,
  setStage,
}: {
  questions?: any[];
  selectedTournament: any;
  userData: any;
  setStage: (n: number) => void;
}) => {
  const [responses, setResponses] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {mutate: submitQuestions, data: submitResponse} =
    useSaveTournamentQuestionsResponse();

  useEffect(() => {
    if (submitResponse) {
      setStage(2);
    }
  }, [submitResponse, setStage]);

  useEffect(() => {
    if (isSubmitting && submitResponse) {
      setIsSubmitting(false);
    }
  }, [isSubmitting, submitResponse]);

  const mandatoryQuestions = questions?.filter(t => t.is_mandatory) || [];
  const allMandatoryAnswered = mandatoryQuestions.every(
    q => responses[q.id]?.trim().length > 0,
  );

  return (
    <Box data-testid="question-list">
      <CCFlatlist
        data={questions}
        renderItem={({item}) => (
          <Box>
            <CCText>{item?.question_text}</CCText>
            <CCTextInput
              onChangeText={text =>
                setResponses(prev => ({...prev, [item.id]: text}))
              }
              data-testid="question-input"
            />
          </Box>
        )}
        keyExtractor={(_, index) => index.toString()}
      />
      <Spacer spacing={vs(20)} />
      <CCButton
        type="fill"
        data-testid="proceed-btn"
        disabled={!allMandatoryAnswered || isSubmitting}
        onPress={() => {
          setIsSubmitting(true);
          submitQuestions({
            tournament_id: selectedTournament?.registration_tid,
            player_id: userData?.player,
            responses: Object.entries(responses).map(
              ([question_id, response]) => ({
                question_id: Number(question_id),
                response: response as string,
              }),
            ),
            sender: userData?.mobile_number,
          });
        }}>
        {isSubmitting ? 'Submitting...' : 'Proceed'}
      </CCButton>
    </Box>
  );
};

const EntryFeeSelect = ({
  errorMessage,
  categories,
  handlePayment,
  isPaymentProcessing,
  continuePaymentUrl,
  pendingOrder,
  setContinuePaymentUrl,
}: {
  errorMessage: string;
  categories: Record<string, number>;
  handlePayment: (category: string, amount: number) => void;
  isPaymentProcessing: boolean;
  continuePaymentUrl?: string | null;
  pendingOrder?: any;
  setContinuePaymentUrl?: (url: string | null) => void;
}) => {
  const [selectedCategory, setSelectedCategory] = useState<any>(null);

  return (
    <View>
      {continuePaymentUrl && pendingOrder && (
        <>
          <View
            style={{
              backgroundColor: CCColors.Orange + '20',
              padding: 15,
              borderRadius: 8,
              marginBottom: 20,
              borderWidth: 1,
              borderColor: CCColors.Orange,
            }}
            data-testid="continue-payment-banner">
            <CCText
              color={CCColors.Orange}
              fontSize={FontSizes[14]}
              fontWeight="600">
              You have an existing incomplete payment
            </CCText>
            <CCText
              color={CCColors.TextColor.Grey}
              fontSize={FontSizes[12]}
              style={{marginTop: 5}}>
              Complete your previous payment to finish registration
            </CCText>
          </View>
          <CCButton
            type="outline"
            disabled={isPaymentProcessing}
            onPress={() => {
              if (continuePaymentUrl) {
                Linking.openURL(continuePaymentUrl);
                setContinuePaymentUrl(null);
              }
            }}
            style={{borderColor: CCColors.Orange, marginBottom: vs(20)}}
            data-testid="continue-payment-btn">
            <CCText color={CCColors.Orange} fontWeight="600">
              Continue Payment
            </CCText>
          </CCButton>
          <Spacer spacing={vs(10)} />
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              marginBottom: vs(20),
            }}>
            <View
              style={{
                flex: 1,
                height: 1,
                backgroundColor: CCColors.TextColor.LightGrey,
              }}
            />
            <CCText
              style={{marginHorizontal: 10, color: CCColors.TextColor.Grey}}>
              or start new
            </CCText>
            <View
              style={{
                flex: 1,
                height: 1,
                backgroundColor: CCColors.TextColor.LightGrey,
              }}
            />
          </View>
        </>
      )}

      <Spacer spacing={vs(10)} />
      <CCText color={CCColors.Primary.Aqua} fontSize={FontSizes[16]}>
        {errorMessage}
      </CCText>
      <Spacer spacing={vs(30)} />
      <CCFlatlist
        data={Object.entries(categories || {}).map(([key, value]) => ({
          key,
          label: `INR ${value} / ${key}`,
          value,
        }))}
        renderItem={({item}) => (
          <CCRadio
            label={item.label}
            selected={selectedCategory?.key === item.key}
            onToggle={() => setSelectedCategory(item)}
            unselectedColor={CCColors.TextColor.Dark}
            style={{marginBottom: vs(10)}}
            data-testid="category-radio"
          />
        )}
        keyExtractor={(_, index) => index.toString()}
      />
      <Spacer spacing={vs(20)} />
      <CCButton
        type="fill"
        disabled={!selectedCategory || isPaymentProcessing}
        onPress={() => {
          if (isPaymentProcessing) {
            return;
          }
          handlePayment(selectedCategory.key, selectedCategory.value);
        }}
        data-testid="proceed-to-payment-btn">
        {isPaymentProcessing ? 'Processing...' : 'Proceed to Payment'}
      </CCButton>
    </View>
  );
};

const SeriesRegistrationModal = ({tournament}: {tournament: any}) => (
  <View style={{alignItems: 'center', justifyContent: 'center'}}>
    <Spacer spacing={vs(20)} />
    <Box alignItems="center" justifyContent="center">
      <Image
        source={require('@/Assets/Images/Svgs/Tournaments.svg')}
        style={{width: 150, height: 150}}
        resizeMode="contain"
      />
    </Box>
    <Spacer spacing={vs(20)} />
    <CCText
      fontSize={20}
      fontWeight={600}
      style={{textAlign: 'center', lineHeight: 28}}>
      Register for Tournament Series
    </CCText>
    <Spacer spacing={vs(10)} />
    <CCText
      fontSize={16}
      color={CCColors.TextColor.LightGrey}
      style={{textAlign: 'center', lineHeight: 24}}>
      {`This tournament is part of the "${
        tournament?.parentName || tournament?.name
      }" series. You need to be registered in the series to play.`}
    </CCText>
    <CCButton
      type="fill"
      onPress={() =>
        Linking.openURL(
          'https://cc-tournaments.s3.ap-south-1.amazonaws.com/brochures/50108.pdf',
        )
      }>
      View Brochure
    </CCButton>
    <Spacer spacing={vs(30)} />
    <Box style={{width: '100%'}}>
      <Spacer spacing={vs(15)} />
      <CCButton
        type="outline"
        onPress={() => Linking.openURL('https://circlechess.com/play-to-win')}
        style={{borderColor: '#25D366'}}>
        <CCText color="#25D366" fontWeight={600}>
          Register
        </CCText>
      </CCButton>
    </Box>
  </View>
);

const PaymentStatusModal = ({
  status,
  onClose,
  pendingOrder,
  onRetry,
  isProcessing,
}: {
  status: string | undefined;
  onClose: () => void;
  pendingOrder?: any;
  onRetry?: () => void;
  isProcessing?: boolean;
}) => {
  const isSuccess = status === 'CHARGED';
  const isPending =
    status === 'PENDING' || isProcessing || status === 'PROCESSING';
  const isFailed = status === 'FAILED';

  return (
    <View
      style={{alignItems: 'center', justifyContent: 'center'}}
      data-testid="payment-status">
      <Spacer spacing={vs(20)} />
      <Box alignItems="center" justifyContent="center">
        <MaterialCommunityIcons
          name={
            isSuccess
              ? 'check-circle'
              : isPending
              ? 'clock-outline'
              : 'close-circle'
          }
          size={56}
          color={
            isSuccess
              ? CCColors.Green
              : isPending
              ? CCColors.Orange
              : CCColors.Red
          }
          data-testid={
            isSuccess
              ? 'success-icon'
              : isPending
              ? 'pending-icon'
              : 'failed-icon'
          }
        />
      </Box>
      <Spacer spacing={vs(30)} />
      <CCText
        fontSize={22}
        fontWeight={600}
        style={{textAlign: 'center', lineHeight: 30}}>
        {isSuccess
          ? 'Payment Successful!'
          : isPending
          ? 'Payment Processing'
          : 'Payment Failed'}
      </CCText>
      <Spacer spacing={vs(15)} />
      <CCText
        fontSize={16}
        color={CCColors.TextColor.LightGrey}
        style={{textAlign: 'center', lineHeight: 24}}>
        {isSuccess
          ? 'Your registration has been confirmed. Good luck with your tournament!'
          : isPending
          ? 'Please wait while we confirm your payment. Do not close this page.'
          : 'We could not process your payment at this moment. Please try again later or contact support if the issue persists.'}
      </CCText>
      <Spacer spacing={vs(40)} />
      <View
        style={{
          width: '100%',
          flexDirection: 'row',
          alignItems: 'center',
          gap: vs(10),
          justifyContent: 'center',
        }}>
        {isFailed && pendingOrder && onRetry && (
          <CCButton type="outline" onPress={onRetry} style={{flex: 1}}>
            <CCText color={CCColors.Primary} fontWeight="600">
              Retry Payment
            </CCText>
          </CCButton>
        )}
        <CCButton type="fill" onPress={onClose} style={{flex: 1}}>
          {isSuccess ? 'Great!' : 'Close'}
        </CCButton>
      </View>
    </View>
  );
};

export default PaidTournamentRegistrationModal;
